import React from 'react';
import { Box, Grid, Paper, Typography, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Chip } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { money, label } from '../../utils/format';
import { MiniStat } from './common';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

function BreakdownList({ title, rows, color, empty }) {
  const max = rows.length ? Math.max(...rows.map((r) => Number(r.total || 0))) : 0;
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>{title}</Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{empty}</Typography>
      ) : rows.map((r) => (
        <Box key={r.category} sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>{label(r.category)}</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ color }}>{money(r.total)}</Typography>
          </Box>
          <LinearProgress variant="determinate" value={max > 0 ? (Number(r.total) / max) * 100 : 0}
            sx={{ height: 8, borderRadius: 4, bgcolor: '#eceff1', '& .MuiLinearProgress-bar': { bgcolor: color } }} />
        </Box>
      ))}
    </Paper>
  );
}

export default function OverviewTab({ summary, trend }) {
  if (!summary) return <Typography color="text.secondary">Loading...</Typography>;

  const chartData = {
    labels: trend.map((t) => t.label),
    datasets: [
      { label: 'Income (fees + other)', data: trend.map((t) => Number(t.feeIncome) + Number(t.otherIncome)), backgroundColor: '#26a69a', borderRadius: 4 },
      { label: 'Outflow (expenses + salaries)', data: trend.map((t) => Number(t.expense) + Number(t.salary)), backgroundColor: '#ef6c00', borderRadius: 4 },
    ],
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${money(c.parsed.y)}` } } },
    scales: { y: { beginAtZero: true, ticks: { callback: (v) => (v >= 100000 ? `${v / 100000}L` : v >= 1000 ? `${v / 1000}k` : v) } } },
  };

  const totalIn = Number(summary.totalIncome || 0);
  const totalOut = Number(summary.totalOutflow || 0);

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}><MiniStat label="Fee collection" value={money(summary.feeIncome)} color="#00796b" bg="#e8f5e9" /></Grid>
        <Grid item xs={12} sm={6} md={3}><MiniStat label="Other income" value={money(summary.otherIncome)} color="#0277bd" bg="#e1f5fe" /></Grid>
        <Grid item xs={12} sm={6} md={3}><MiniStat label="Operating expenses" value={money(summary.totalExpense)} color="#ef6c00" bg="#fff3e0" /></Grid>
        <Grid item xs={12} sm={6} md={3}><MiniStat label="Salaries paid" value={money(summary.salaryPaid)} color="#6a1b9a" bg="#f3e5f5" /></Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>Income and outflow, last 12 months</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip size="small" label={`Salaries pending: ${money(summary.pendingSalary)}`} sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700 }} />
            <Chip size="small" label={`Fee dues: ${money(summary.feeDue)}`} sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 700 }} />
          </Box>
        </Box>
        <Box sx={{ height: 280 }}><Bar data={chartData} options={chartOptions} /></Box>
      </Paper>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <BreakdownList title="Income by category (selected period)" rows={summary.incomeByCategory || []} color="#00796b" empty="No income in this period." />
        </Grid>
        <Grid item xs={12} md={6}>
          <BreakdownList title="Outflow by category (selected period)" rows={summary.outflowByCategory || []} color="#ef6c00" empty="No expenses in this period." />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ p: 2 }}>Cash, bank and UPI position (selected period)</Typography>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Payment mode</TableCell><TableCell align="right">Received</TableCell><TableCell align="right">Paid out</TableCell><TableCell align="right">Net</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {(summary.byPaymentMode || []).map((m) => (
                  <TableRow key={m.mode}>
                    <TableCell>{label(m.mode)}</TableCell>
                    <TableCell align="right" sx={{ color: '#00796b' }}>{money(m.inflow)}</TableCell>
                    <TableCell align="right" sx={{ color: '#ef6c00' }}>{money(m.outflow)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{money(Number(m.inflow) - Number(m.outflow))}</TableCell>
                  </TableRow>
                ))}
                {(!summary.byPaymentMode || summary.byPaymentMode.length === 0) && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No transactions in this period</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Period at a glance</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography variant="body2">Opening balance</Typography><Typography variant="body2" fontWeight={700}>{money(summary.openingBalance)}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography variant="body2" color="#00796b">+ Income</Typography><Typography variant="body2" fontWeight={700} color="#00796b">{money(totalIn)}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography variant="body2" color="#ef6c00">− Outflow</Typography><Typography variant="body2" fontWeight={700} color="#ef6c00">{money(totalOut)}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '2px solid #004d40' }}>
              <Typography variant="body2" fontWeight={800}>Closing balance</Typography>
              <Typography variant="body2" fontWeight={800}>{money(Number(summary.openingBalance) + totalIn - totalOut)}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Balance = all money received minus all money paid since the books were opened.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
